import styled from "styled-components";
import { AreaChart, Area, BarChart, Bar, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import theme from "../theme";
import { mobile } from "../responsive";
import { IconDocument, IconEye, IconHeart, IconUsers, IconClock, IconMessage, IconStar } from "../icons";

const Wrap = styled.section`
  margin-bottom: 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Hero = styled.div`
  background: linear-gradient(135deg, ${theme.cardBg} 0%, #fff9f1 55%, #f7efe4 100%);
  border: 1.5px solid ${theme.cardBorder};
  border-radius: 18px;
  padding: 22px 22px 18px;
  box-shadow: 0 1px 8px rgba(26, 18, 8, 0.05);
`;

const HeroTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
  ${mobile(`flex-direction: column;`)}
`;

const HeroCopy = styled.div`
  min-width: 0;
`;

const Eyebrow = styled.div`
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${theme.primary};
  margin-bottom: 6px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 850;
  letter-spacing: -0.5px;
  color: ${theme.textMain};
`;

const Subtitle = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  color: ${theme.textMuted};
  line-height: 1.55;
  max-width: 720px;
`;

const HeroMetric = styled.div`
  background: rgba(160, 98, 10, 0.08);
  border: 1px solid rgba(160, 98, 10, 0.15);
  border-radius: 14px;
  padding: 12px 14px;
  min-width: 160px;
  flex-shrink: 0;
`;

const HeroMetricLabel = styled.div`
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${theme.textMuted};
  margin-bottom: 6px;
`;

const HeroMetricValue = styled.div`
  font-size: 24px;
  font-weight: 850;
  color: ${theme.textMain};
  line-height: 1;
`;

const HeroMetricSub = styled.div`
  font-size: 12px;
  color: ${theme.textSub};
  margin-top: 4px;
`;

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  ${mobile(`grid-template-columns: repeat(2, minmax(0, 1fr));`)}
  @media (max-width: 960px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const MetricCard = styled.div`
  background: ${theme.cardBg};
  border: 1.5px solid ${theme.cardBorder};
  border-radius: 14px;
  padding: 14px;
  box-shadow: 0 1px 8px rgba(26, 18, 8, 0.04);
`;

const MetricTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
`;

const MetricIcon = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${theme.primaryLight};
  color: ${theme.primary};
`;

const MetricValue = styled.div`
  font-size: 21px;
  font-weight: 850;
  color: ${theme.textMain};
  letter-spacing: -0.3px;
`;

const MetricLabel = styled.div`
  font-size: 11px;
  color: ${theme.textMuted};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const MetricFoot = styled.div`
  margin-top: 6px;
  font-size: 11px;
  color: ${theme.textSub};
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 16px;
  ${mobile(`grid-template-columns: 1fr;`)}
`;

const Panel = styled.div`
  background: ${theme.cardBg};
  border: 1.5px solid ${theme.cardBorder};
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 1px 8px rgba(26, 18, 8, 0.04);
`;

const PanelTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
`;

const PanelHeading = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: ${theme.textMain};
`;

const PanelSub = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${theme.textMuted};
`;

const ChartShell = styled.div`
  width: 100%;
  height: 280px;
`;

const DualChart = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  ${mobile(`grid-template-columns: 1fr;`)}
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const NoteRow = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px;
  border: 1px solid ${theme.separator};
  border-radius: 12px;
  background: ${theme.bg};
`;

const NoteBadge = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: ${theme.primaryLight};
  color: ${theme.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const NoteMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

const NoteTitle = styled.div`
  font-size: 13px;
  font-weight: 800;
  color: ${theme.textMain};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NoteSub = styled.div`
  margin-top: 3px;
  font-size: 11.5px;
  color: ${theme.textMuted};
`;

const ScoreBar = styled.div`
  height: 6px;
  border-radius: 999px;
  background: ${theme.bgAlt};
  overflow: hidden;
  margin-top: 10px;
`;

const ScoreFill = styled.div`
  height: 100%;
  width: ${(p) => p.$pct}%;
  border-radius: inherit;
  background: linear-gradient(90deg, ${theme.primary} 0%, #d08d2d 100%);
`;

const EmptyState = styled.div`
  padding: 24px 8px;
  text-align: center;
  color: ${theme.textMuted};
  font-size: 13px;
`;

function formatNumber(value) {
  return new Intl.NumberFormat(undefined, { notation: value >= 1000 ? "compact" : "standard" }).format(value || 0);
}

function toShortDate(dateValue) {
  return new Date(dateValue).toLocaleDateString([], { month: "short", day: "numeric" });
}

function scoreNote(note, commentCounts) {
  const likes = note.likes?.length || 0;
  const views = note.views?.length || 0;
  const comments = commentCounts?.[note._id] || 0;
  const ratingScore = note.ratings?.length
    ? note.ratings.reduce((sum, rating) => sum + Number(rating.value || 0), 0) / note.ratings.length
    : 0;
  return (likes * 3) + (views * 1.2) + (comments * 2.2) + (ratingScore * 4);
}

function buildTrendData(notes, commentCounts) {
  const days = Array.from({ length: 7 }, (_, index) => {
    const day = new Date();
    day.setDate(day.getDate() - (6 - index));
    return {
      label: day.toLocaleDateString([], { weekday: "short" }),
      key: day.toDateString(),
      notes: 0,
      engagement: 0,
    };
  });

  notes.forEach((note) => {
    const created = new Date(note.createdAt);
    const daysAgo = Math.floor((Date.now() - created.getTime()) / 86400000);
    if (daysAgo < 7) {
      const bucket = days[6 - daysAgo];
      bucket.notes += 1;
      bucket.engagement += scoreNote(note, commentCounts);
    }
  });

  return days;
}

function TooltipContent({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#fff", border: `1px solid ${theme.cardBorder}`, borderRadius: 10, padding: "8px 10px", boxShadow: "0 10px 24px rgba(26,18,8,0.12)" }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: theme.textMain, marginBottom: 4 }}>{label}</div>
      {payload.map((item) => (
        <div key={item.dataKey} style={{ fontSize: 12, color: item.color }}>
          {item.name}: {formatNumber(item.value)}
        </div>
      ))}
    </div>
  );
}

export default function AnalyticsDashboard({ currentUser, myNotes = [], commentCounts = {} }) {
  const totalLikes = myNotes.reduce((sum, note) => sum + (note.likes?.length || 0), 0);
  const totalViews = myNotes.reduce((sum, note) => sum + (note.views?.length || 0), 0);
  const totalComments = myNotes.reduce((sum, note) => sum + (commentCounts[note._id] || 0), 0);
  const followerCount = currentUser?.followers?.length || 0;
  const avgRating = myNotes.length
    ? myNotes.reduce((sum, note) => {
        const noteAverage = note.ratings?.length
          ? note.ratings.reduce((ratingSum, rating) => ratingSum + Number(rating.value || 0), 0) / note.ratings.length
          : 0;
        return sum + noteAverage;
      }, 0) / myNotes.length
    : 0;

  const trendData = buildTrendData(myNotes, commentCounts);
  const topNotes = [...myNotes]
    .map((note) => ({
      ...note,
      score: scoreNote(note, commentCounts),
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 4);

  const recentNotes = [...myNotes]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 4);

  const bestScore = topNotes[0]?.score || 1;

  return (
    <Wrap>
      <Hero>
        <HeroTop>
          <HeroCopy>
            <Eyebrow>Analytics dashboard</Eyebrow>
            <Title>Track your notes, engagement, and reach.</Title>
            <Subtitle>
              This dashboard turns your note activity into something measurable: publishing pace, audience reach, likes,
              comments, and quality signals from ratings.
            </Subtitle>
          </HeroCopy>
          <HeroMetric>
            <HeroMetricLabel>Current reach</HeroMetricLabel>
            <HeroMetricValue>{formatNumber(followerCount + totalViews)}</HeroMetricValue>
            <HeroMetricSub>Followers + total views</HeroMetricSub>
          </HeroMetric>
        </HeroTop>

        <MetricGrid>
          <MetricCard>
            <MetricTop>
              <MetricIcon><IconDocument size="16px" /></MetricIcon>
            </MetricTop>
            <MetricValue>{formatNumber(myNotes.length)}</MetricValue>
            <MetricLabel>Notes published</MetricLabel>
            <MetricFoot>{recentNotes.length > 0 ? `Latest: ${recentNotes[0].notename}` : "No notes yet"}</MetricFoot>
          </MetricCard>
          <MetricCard>
            <MetricTop>
              <MetricIcon><IconHeart size="16px" /></MetricIcon>
            </MetricTop>
            <MetricValue>{formatNumber(totalLikes)}</MetricValue>
            <MetricLabel>Total likes</MetricLabel>
            <MetricFoot>Across all of your notes</MetricFoot>
          </MetricCard>
          <MetricCard>
            <MetricTop>
              <MetricIcon><IconEye size="16px" /></MetricIcon>
            </MetricTop>
            <MetricValue>{formatNumber(totalViews)}</MetricValue>
            <MetricLabel>Total views</MetricLabel>
            <MetricFoot>Audience exposure and discoverability</MetricFoot>
          </MetricCard>
          <MetricCard>
            <MetricTop>
              <MetricIcon><IconMessage size="16px" /></MetricIcon>
            </MetricTop>
            <MetricValue>{formatNumber(totalComments)}</MetricValue>
            <MetricLabel>Comments</MetricLabel>
            <MetricFoot>Conversation started around your work</MetricFoot>
          </MetricCard>
          <MetricCard>
            <MetricTop>
              <MetricIcon><IconUsers size="16px" /></MetricIcon>
            </MetricTop>
            <MetricValue>{formatNumber(followerCount)}</MetricValue>
            <MetricLabel>Followers</MetricLabel>
            <MetricFoot>People who receive your updates</MetricFoot>
          </MetricCard>
        </MetricGrid>
      </Hero>

      <MainGrid>
        <Panel>
          <PanelTitle>
            <div>
              <PanelHeading>Engagement trends</PanelHeading>
              <PanelSub>Creation and engagement signals from the last 7 days.</PanelSub>
            </div>
            <MetricIcon title="Average rating" style={{ width: 40, height: 40 }}>
              <IconStar size="16px" filled />
            </MetricIcon>
          </PanelTitle>
          {myNotes.length === 0 ? (
            <EmptyState>No note analytics yet. Publish your first note to see trend data.</EmptyState>
          ) : (
            <DualChart>
              <ChartShell>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trendData} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="trendNotes" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={theme.primary} stopOpacity={0.28} />
                        <stop offset="95%" stopColor={theme.primary} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: theme.textMuted }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: theme.textMuted }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <CartesianGrid stroke={theme.separator} strokeDasharray="4 4" vertical={false} />
                    <Tooltip content={<TooltipContent />} />
                    <Area type="monotone" dataKey="notes" name="Notes" stroke={theme.primary} fill="url(#trendNotes)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartShell>
              <ChartShell>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trendData} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: theme.textMuted }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: theme.textMuted }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <CartesianGrid stroke={theme.separator} strokeDasharray="4 4" vertical={false} />
                    <Tooltip content={<TooltipContent />} />
                    <Bar dataKey="engagement" name="Engagement" fill={theme.primary} radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartShell>
            </DualChart>
          )}
        </Panel>

        <Panel>
          <PanelTitle>
            <div>
              <PanelHeading>Quality snapshot</PanelHeading>
              <PanelSub>Ratings give a quick signal on how your notes are landing.</PanelSub>
            </div>
          </PanelTitle>

          <List>
            <NoteRow>
              <NoteBadge><IconStar size="16px" filled /></NoteBadge>
              <NoteMeta>
                <NoteTitle>{avgRating ? avgRating.toFixed(1) : "0.0"} average rating</NoteTitle>
                <NoteSub>Average across your note library</NoteSub>
                <ScoreBar>
                  <ScoreFill $pct={Math.min((avgRating / 5) * 100, 100)} />
                </ScoreBar>
              </NoteMeta>
            </NoteRow>
            <NoteRow>
              <NoteBadge><IconClock size="16px" /></NoteBadge>
              <NoteMeta>
                <NoteTitle>{recentNotes.length ? toShortDate(recentNotes[0].createdAt) : "No recent posts"}</NoteTitle>
                <NoteSub>{recentNotes.length ? `Latest note: ${recentNotes[0].notename}` : "Publish something new to refresh the feed"}</NoteSub>
                <ScoreBar>
                  <ScoreFill $pct={Math.min(recentNotes.length * 25, 100)} />
                </ScoreBar>
              </NoteMeta>
            </NoteRow>
          </List>
        </Panel>
      </MainGrid>

      <MainGrid>
        <Panel>
          <PanelTitle>
            <div>
              <PanelHeading>Top notes</PanelHeading>
              <PanelSub>Ranked by likes, views, comments, and ratings.</PanelSub>
            </div>
          </PanelTitle>
          {topNotes.length === 0 ? (
            <EmptyState>No ranked notes yet.</EmptyState>
          ) : (
            <List>
              {topNotes.map((note) => (
                <NoteRow key={note._id}>
                  <NoteBadge><IconDocument size="16px" /></NoteBadge>
                  <NoteMeta>
                    <NoteTitle>{note.notename}</NoteTitle>
                    <NoteSub>
                      {formatNumber(note.likes?.length || 0)} likes · {formatNumber(note.views?.length || 0)} views · {formatNumber(commentCounts[note._id] || 0)} comments
                    </NoteSub>
                    <ScoreBar>
                      <ScoreFill $pct={Math.max(10, Math.min((note.score / bestScore) * 100, 100))} />
                    </ScoreBar>
                  </NoteMeta>
                </NoteRow>
              ))}
            </List>
          )}
        </Panel>

        <Panel>
          <PanelTitle>
            <div>
              <PanelHeading>Recent activity</PanelHeading>
              <PanelSub>What you published most recently.</PanelSub>
            </div>
          </PanelTitle>
          {recentNotes.length === 0 ? (
            <EmptyState>No recent notes to show.</EmptyState>
          ) : (
            <List>
              {recentNotes.map((note) => (
                <NoteRow key={note._id}>
                  <NoteBadge><IconClock size="16px" /></NoteBadge>
                  <NoteMeta>
                    <NoteTitle>{note.notename}</NoteTitle>
                    <NoteSub>{new Date(note.createdAt).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}</NoteSub>
                    <ScoreBar>
                      <ScoreFill $pct={Math.max(10, Math.min((scoreNote(note, commentCounts) / (bestScore || 1)) * 100, 100))} />
                    </ScoreBar>
                  </NoteMeta>
                </NoteRow>
              ))}
            </List>
          )}
        </Panel>
      </MainGrid>
    </Wrap>
  );
}
